import { TextureColorSpace, TextureFormat } from '@seddi/surface-material';
import { type LoadingManager, SRGBColorSpace, Texture, type WebGLRenderer } from '@seddi/three';
import { EXRLoader } from '@seddi/three/addons/loaders/EXRLoader.js';
import { HDRLoader } from '@seddi/three/addons/loaders/HDRLoader.js';
import log from 'loglevel';

import type { TextureDesc } from '../domain/materialTypes';
import { ImageBitmapLoader } from '../three/loaders/imageBitmapLoader';
import type { TextureImage } from '../three/materials/materialTypes';
import { premultiplyAlphaSRGB } from './texturePremultipliedAlpha';

type Options = { premultiplyAlpha?: 'none' | 'premultiply' };

/**
 * Load texture.
 * @param desc Texture desc.
 * @param manager Loading manager.
 * @param options Texture options.
 */
export const load = async (
  desc: TextureDesc,
  manager: LoadingManager,
  renderer: WebGLRenderer,
  options?: Options,
): Promise<Texture<TextureImage>> => {
  let texture!: Texture<TextureImage>;

  // Prioritize image bitmap if available.
  if (desc.imageBitmap) {
    texture = new Texture(desc.imageBitmap);
    texture.flipY = false;
    texture.needsUpdate = true;
  } else {
    switch (desc.format) {
      case TextureFormat.EXR: {
        const loader = new EXRLoader(manager);
        texture = await loader.loadAsync(desc.href);

        if (!texture) {
          log.error(`Error loading texture ${desc.href}`);
          return undefined;
        }

        break;
      }
      case TextureFormat.HDR: {
        const loader = new HDRLoader(manager);
        texture = await loader.loadAsync(desc.href);

        if (!texture) {
          log.error(`Error loading texture ${desc.href}`);
          return undefined;
        }

        break;
      }
      case TextureFormat.JPEG:
      case TextureFormat.PNG:
      case TextureFormat.WEBP: {
        const loader = new ImageBitmapLoader(manager);
        loader.setOptions({ imageOrientation: 'flipY', premultiplyAlpha: 'none' });
        const image = await loader.loadAsync(desc.href);
        if (!image) {
          log.error(`Error loading texture ${desc.href}`);
          return undefined;
        }
        texture = new Texture(image);
        texture.flipY = false;
        texture.needsUpdate = true;

        break;
      }
      default: {
        log.error(`Unrecognized texture format ${desc.format}`);
        return undefined;
      }
    }
  }

  if (desc.colorSpace === TextureColorSpace.GAMMA) {
    texture.colorSpace = SRGBColorSpace;

    // Handle premultiplied alpha with shader when color space is sRGB
    if (options?.premultiplyAlpha === 'premultiply') {
      const premultipliedTexture = premultiplyAlphaSRGB(renderer, texture as Texture<TextureImage>);
      texture.dispose();
      texture = premultipliedTexture;
    }
  }

  return texture;
};
